import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Send, Loader2, Clock, ShieldCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import POBulkDecisionDialog from './POBulkDecisionDialog';

/**
 * POApprovalPanel — drop-in section for the Accounting page.
 *  - Top: "Awaiting my decision" — POs where the current admin's email is a
 *    pending approver; one-click Approve / Reject inline.
 *  - Bottom: "Submit for approval" quick modal for any draft PO id.
 *
 * Backend contract:
 *  GET  /api/admin/accounting/purchase-orders/pending-my-approval
 *  POST /api/admin/accounting/purchase-orders/{id}/decide           {decision, note?}
 *  POST /api/admin/accounting/purchase-orders/{id}/submit-for-approval
 *                                                                    {approver_emails[], approval_mode}
 */
export default function POApprovalPanel({ api, onDecided }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decideId, setDecideId] = useState(null);   // po_id being decided
  const [note, setNote] = useState('');
  const [sellPromptPO, setSellPromptPO] = useState(null); // PO awaiting selling-price prompt on approve
  const [selected, setSelected] = useState([]);
  const [bulkDecision, setBulkDecision] = useState(null);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/accounting/purchase-orders/pending-my-approval');
      const list = res.data || [];
      setPending(list);
      setSelected(prev => prev.filter(id => list.some(p => p._id === id)));
    } catch (e) {
      // Silent — this endpoint is optional
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchPending(); }, []);

  const sendDecision = async (poId, decision, extra = {}) => {
    try {
      await api.post(`/api/admin/accounting/purchase-orders/${poId}/decide`, { decision, note, ...extra });
      toast.success(decision === 'approved' ? 'Approved — everyone notified' : 'Rejected — everyone notified');
      setDecideId(null); setNote(''); setSellPromptPO(null);
      fetchPending();
      onDecided?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decision failed');
    }
  };

  const decide = (po, decision) => {
    if (decision === 'approved') {
      const missing = (po.items || []).some(it => !it.selling_price || Number(it.selling_price) <= 0);
      if (missing) {
        // Open selling-price prompt so approver can fill customer-facing prices
        setSellPromptPO(po);
        return;
      }
    }
    sendDecision(po._id, decision);
  };

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allOn = pending.length > 0 && selected.length === pending.length;
  const selectedPOs = pending.filter(p => selected.includes(p._id));
  const selectedTotal = selectedPOs.reduce((s, p) => s + Number(p.total || 0), 0);

  const runBulk = async (bulkNote) => {
    try {
      const r = await api.post('/api/admin/accounting/purchase-orders/bulk-decide', { po_ids: selected, decision: bulkDecision, note: bulkNote });
      const { done = [], skipped = [] } = r.data || {};
      const verb = bulkDecision === 'approved' ? 'approved' : 'rejected';
      if (done.length) toast.success(`${done.length} PO${done.length > 1 ? 's' : ''} ${verb}${skipped.length ? `, ${skipped.length} skipped` : ''}`);
      skipped.forEach(sk => toast.warning(`${sk.po_number || 'PO'} skipped: ${sk.reason}`));
      if (!done.length && !skipped.length) toast.info('Nothing to update');
      setBulkDecision(null); setSelected([]);
      fetchPending();
      onDecided?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk decision failed');
    }
  };

  if (loading) return null;
  if (pending.length === 0) return null;

  return (
    <Card className="p-5 mb-5 border-2 border-fuchsia-300 bg-white shadow-sm" data-testid="po-approval-panel">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-fuchsia-100">
        <ShieldCheck className="w-5 h-5 text-fuchsia-700" />
        <h2 className="text-base font-bold text-neutral-900">Awaiting your approval</h2>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-fuchsia-600 text-white font-semibold shadow-sm">{pending.length}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="po-approval-bulk-bar">
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 cursor-pointer select-none">
          <input type="checkbox" checked={allOn} onChange={(e) => setSelected(e.target.checked ? pending.map(p => p._id) : [])}
            className="w-4 h-4 accent-fuchsia-600" data-testid="po-approval-select-all" />
          Select all
        </label>
        {selected.length > 0 && (
          <div className="inline-flex flex-wrap items-center gap-2 ml-auto px-3 py-1.5 rounded-full bg-fuchsia-100 border border-fuchsia-200 animate-in fade-in">
            <span className="text-xs font-semibold text-fuchsia-900" data-testid="po-approval-selected-count">{selected.length} selected · ₹{selectedTotal.toLocaleString('en-IN')}</span>
            <button onClick={() => setBulkDecision('approved')} className="text-xs px-2.5 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold active:scale-95 transition" data-testid="po-approval-bulk-approve">Approve selected</button>
            <button onClick={() => setBulkDecision('rejected')} className="text-xs px-2.5 py-1 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold active:scale-95 transition" data-testid="po-approval-bulk-reject">Reject selected</button>
            <button onClick={() => setSelected([])} className="text-xs px-2.5 py-1 rounded-full bg-white hover:bg-neutral-100 text-neutral-700 border border-neutral-200" data-testid="po-approval-bulk-clear">Clear</button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {pending.map(po => (
          <div key={po._id} className={`rounded-xl border p-4 transition-colors ${selected.includes(po._id) ? 'bg-fuchsia-100/70 border-fuchsia-400' : 'bg-fuchsia-50/60 border-fuchsia-200'}`} data-testid={`po-pending-${po._id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <input type="checkbox" checked={selected.includes(po._id)} onChange={() => toggle(po._id)}
                className="w-4 h-4 mt-0.5 accent-fuchsia-600 shrink-0" data-testid={`po-approval-select-${po._id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-neutral-900">{po.po_number}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {po.approval_mode === 'all' ? 'All approvers required' : 'Any approver can decide'}
                  </span>
                  {po.category && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-800 font-medium">{po.category}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Client</div>
                    <div className="text-neutral-900 font-medium truncate">{po.client_name || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Vendor</div>
                    <div className="text-neutral-900 font-medium truncate">{po.vendor_name || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Amount</div>
                    <div className="text-neutral-900 font-bold">₹{Number(po.total || 0).toLocaleString('en-IN')}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Submitted by</div>
                    <div className="text-neutral-700 truncate">{po.submitted_by_name || 'Admin'}</div>
                  </div>
                </div>
                {po.notes && <p className="text-xs text-neutral-700 mt-2 italic border-l-2 border-fuchsia-300 pl-2">“{po.notes}”</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={() => setDecideId(po._id === decideId ? null : po._id)} variant="outline" data-testid={`po-note-toggle-${po._id}`}>
                  Add note
                </Button>
                <Button size="sm" onClick={() => decide(po, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`po-approve-${po._id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                </Button>
                <Button size="sm" onClick={() => decide(po, 'rejected')} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`po-reject-${po._id}`}>
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              </div>
            </div>
            {decideId === po._id && (
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (goes to all approvers + submitter)" rows={2} className="mt-3" data-testid={`po-note-${po._id}`} />
            )}
          </div>
        ))}
      </div>
      <POBulkDecisionDialog decision={bulkDecision} pos={selectedPOs} onClose={() => setBulkDecision(null)} onConfirm={runBulk} />
      {sellPromptPO && (
        <SellingPricePrompt
          po={sellPromptPO}
          onSkip={() => sendDecision(sellPromptPO._id, 'approved')}
          onConfirm={(line_prices) => sendDecision(sellPromptPO._id, 'approved', { line_prices })}
          onClose={() => setSellPromptPO(null)}
        />
      )}
    </Card>
  );
}

// ---- Selling price prompt modal (shown when approver hits Approve on a PO
// whose items don't yet carry a customer-facing selling price) --------------
function SellingPricePrompt({ po, onSkip, onConfirm, onClose }) {
  const [prices, setPrices] = useState(() => {
    const initial = {};
    (po.items || []).forEach((it, i) => { initial[i] = it.selling_price ? String(it.selling_price) : ''; });
    return initial;
  });
  const items = po.items || [];
  const totalCost = items.reduce((s, it) => s + Number(it.costing || 0) * Number(it.quantity || 0), 0);
  const totalSell = items.reduce((s, it, i) => s + (Number(prices[i]) || 0) * Number(it.quantity || 0), 0);

  const submit = () => {
    const line_prices = {};
    Object.entries(prices).forEach(([k, v]) => {
      if (v !== '' && !Number.isNaN(Number(v))) line_prices[k] = Number(v);
    });
    onConfirm(line_prices);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="po-sell-prompt-backdrop">
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="po-sell-prompt">
        <div className="p-6 border-b border-neutral-100">
          <h2 className="text-lg font-bold text-neutral-900">Add selling prices?</h2>
          <p className="text-xs text-neutral-500 mt-1">
            You can fill the customer-facing selling price for each line item now, or skip and confirm approval with cost prices only.
          </p>
        </div>
        <div className="p-6 max-h-[50vh] overflow-y-auto space-y-3">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-neutral-900 truncate">{it.vendor_name || `Item ${i+1}`}</div>
                <div className="text-[11px] text-neutral-500">
                  Qty: {it.quantity} · Cost: ₹{Number(it.costing || 0).toLocaleString('en-IN')}
                </div>
              </div>
              <div className="w-32">
                <Input
                  type="number"
                  value={prices[i]}
                  onChange={(e) => setPrices({ ...prices, [i]: e.target.value })}
                  placeholder="Selling ₹"
                  data-testid={`po-sell-price-${i}`}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-neutral-100 bg-neutral-50 grid grid-cols-2 gap-2 text-xs">
          <div>Total cost: <span className="font-semibold">₹{totalCost.toLocaleString('en-IN')}</span></div>
          <div className="text-right">Total selling: <span className="font-semibold text-emerald-700">₹{totalSell.toLocaleString('en-IN')}</span></div>
        </div>
        <div className="p-6 border-t border-neutral-100 flex gap-2 justify-end bg-neutral-50/50 rounded-b-3xl">
          <Button variant="outline" onClick={onSkip} data-testid="po-sell-skip">Skip &amp; approve</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="po-sell-confirm">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm with prices
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * POApprovalHistory — inline vertical timeline of every decision on a PO.
 * Used both by the awaiting-panel and by the row-expand feature in
 * `AccountingPage`. Shows submitter → each approver's decision + note + timestamp.
 */
export function POApprovalHistory({ po }) {
  if (!po) return null;
  const events = [];
  if (po.submitted_at) {
    events.push({
      when: po.submitted_at,
      color: 'bg-fuchsia-500',
      label: 'Submitted for approval',
      who: po.submitted_by_name || 'Admin',
      note: `Mode: ${po.approval_mode === 'all' ? 'Both must approve' : 'Either can approve'} · ${(po.approver_emails||[]).length} approver${(po.approver_emails||[]).length===1?'':'s'}`,
    });
  }
  for (const a of (po.approvals || [])) {
    events.push({
      when: a.decided_at,
      color: a.decision === 'approved' ? 'bg-emerald-500' : 'bg-red-500',
      label: a.decision === 'approved' ? 'Approved' : 'Rejected',
      who: a.decided_by || a.email,
      note: a.note || '',
    });
  }
  const pendingApprovers = (po.approver_emails || []).filter(e => !(po.approvals || []).some(a => (a.email||'').toLowerCase() === e.toLowerCase()));
  if (po.approval_status === 'pending') {
    for (const email of pendingApprovers) {
      events.push({ when: null, color: 'bg-neutral-300', label: 'Waiting on', who: email, note: '', pending: true });
    }
  }

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return String(iso); }
  };

  if (events.length === 0) {
    return <div className="text-xs text-neutral-500 italic py-2">No approval activity yet.</div>;
  }

  return (
    <div className="pl-2" data-testid={`po-history-${po._id}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-3 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" /> Approval Timeline
      </div>
      <ol className="relative border-l-2 border-neutral-200 pl-4 space-y-3">
        {events.map((e, i) => (
          <li key={i} className="relative">
            <span className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full border-2 border-white ${e.color} ${e.pending ? 'animate-pulse' : ''}`} />
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className={`text-xs font-bold ${e.pending ? 'text-neutral-400' : 'text-neutral-900'}`}>{e.label}</span>
              <span className="text-xs text-neutral-700">· {e.who}</span>
              {e.when && <span className="text-[11px] text-neutral-400">· {fmt(e.when)}</span>}
            </div>
            {e.note && <p className="text-[11px] text-neutral-500 italic mt-0.5">“{e.note}”</p>}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * POSubmitForApprovalModal — controlled dialog for accountants/admins to
 * submit a draft PO. Parent passes `po` (or null to hide) and `onDone`.
 *
 * Fetches typeahead suggestions from
 *   GET /api/admin/accounting/purchase-orders/approver-suggestions?q=…
 * and lets the user click-to-add. Manual comma-separated typing still works.
 */
export function POSubmitForApprovalModal({ api, po, onClose, onDone }) {
  // Default: Naman receives every PO for approval by default. Users can add
  // more approvers before hitting submit.
  const DEFAULT_APPROVERS = ['naman@halfeatendonut.in'];
  const [emails, setEmails] = useState(DEFAULT_APPROVERS);  // pre-filled chip list
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [mode, setMode] = useState('any');
  const [saving, setSaving] = useState(false);

  // Debounced typeahead
  useEffect(() => {
    if (!po) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/api/admin/accounting/purchase-orders/approver-suggestions?q=${encodeURIComponent(input)}`);
        if (!cancelled) setSuggestions(res.data || []);
      } catch { /* silent */ }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [input, po, api]);

  if (!po) return null;

  const addEmail = (e) => {
    const clean = (e || '').trim().toLowerCase();
    if (!clean || !clean.includes('@')) return;
    if (emails.includes(clean)) return;
    setEmails([...emails, clean]);
    setInput('');
  };
  const removeEmail = (e) => setEmails(emails.filter(x => x !== e));
  const onKeyDown = (ev) => {
    if ((ev.key === 'Enter' || ev.key === ',') && input.trim()) {
      ev.preventDefault();
      addEmail(input);
    } else if (ev.key === 'Backspace' && !input && emails.length) {
      setEmails(emails.slice(0, -1));
    }
  };
  const availableSuggestions = suggestions.filter(s => !emails.includes(s.email.toLowerCase()));

  const submit = async () => {
    // Merge any pending typed input
    let all = [...emails];
    if (input.trim() && input.includes('@') && !all.includes(input.trim().toLowerCase())) all.push(input.trim().toLowerCase());
    if (all.length === 0) { toast.error('Add at least one approver email'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/admin/accounting/purchase-orders/${po._id}/submit-for-approval`, {
        approver_emails: all, approval_mode: mode,
      });
      toast.success(`Sent to ${all.length} approver${all.length===1?'':'s'} · ${res.data.emails_sent} email${res.data.emails_sent===1?'':'s'} delivered`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-end md:items-center justify-center md:p-4" onClick={onClose} data-testid="po-submit-backdrop">
      <div className="bg-white rounded-t-3xl md:rounded-3xl max-w-md w-full shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="po-submit-modal">
        {/* Mobile drag handle */}
        <div className="md:hidden pt-3 pb-1 flex justify-center">
          <div className="h-1.5 w-10 rounded-full bg-neutral-300" />
        </div>
        <div className="p-6 pt-4 md:pt-6 border-b border-neutral-100">
          <h2 className="text-lg font-bold text-neutral-900">Submit PO for approval</h2>
          <p className="text-xs text-neutral-500 mt-1">{po.po_number} · ₹{Number(po.total||0).toLocaleString('en-IN')}</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Approvers</label>
            <div className="border border-neutral-200 rounded-xl p-2 min-h-[52px] flex flex-wrap gap-1.5 items-center bg-white focus-within:ring-2 focus-within:ring-fuchsia-200">
              {emails.map(e => (
                <span key={e} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-fuchsia-100 text-fuchsia-800 text-xs font-medium" data-testid={`po-approver-chip-${e}`}>
                  {e}
                  <button type="button" onClick={() => removeEmail(e)} className="hover:text-fuchsia-900" aria-label={`Remove ${e}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={() => { if (input.includes('@')) addEmail(input); }}
                placeholder={emails.length === 0 ? 'Type a name or email…' : ''}
                className="flex-1 min-w-[140px] outline-none text-sm bg-transparent"
                data-testid="po-approver-input"
              />
            </div>
            {input.length > 0 && availableSuggestions.length > 0 && (
              <div className="mt-1 bg-white border border-neutral-200 rounded-xl shadow-sm max-h-52 overflow-y-auto" data-testid="po-approver-suggestions">
                {availableSuggestions.slice(0, 8).map(s => (
                  <button
                    type="button"
                    key={s.email}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addEmail(s.email)}
                    className="w-full text-left px-3 py-2 hover:bg-fuchsia-50 border-b border-neutral-100 flex items-center gap-2"
                    data-testid={`po-approver-suggest-${s.email}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 truncate">{s.name}</div>
                      <div className="text-xs text-neutral-500 truncate">{s.email}</div>
                    </div>
                    {s.is_finance && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Finance</span>}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-1.5">Type a name, pick from the suggestions, or paste emails and press Enter.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className={`cursor-pointer border rounded-xl p-3 text-sm ${mode==='any'?'border-fuchsia-500 bg-fuchsia-50':'border-neutral-200'}`}>
              <input type="radio" checked={mode==='any'} onChange={() => setMode('any')} className="mr-2" data-testid="po-mode-any" />
              Either approves
            </label>
            <label className={`cursor-pointer border rounded-xl p-3 text-sm ${mode==='all'?'border-fuchsia-500 bg-fuchsia-50':'border-neutral-200'}`}>
              <input type="radio" checked={mode==='all'} onChange={() => setMode('all')} className="mr-2" data-testid="po-mode-all" />
              Both must approve
            </label>
          </div>
          <p className="text-[11px] text-neutral-500">Approvers get an email with Approve / Reject buttons AND can decide from their Girgit dashboard.</p>
        </div>
        <div className="p-4 md:p-6 border-t border-neutral-100 flex flex-col-reverse md:flex-row gap-2 md:justify-end bg-neutral-50/50 md:rounded-b-3xl safe-bottom">
          <Button variant="outline" onClick={onClose} className="w-full md:w-auto" data-testid="po-submit-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white w-full md:w-auto" data-testid="po-submit-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-3.5 h-3.5 mr-1" /> Send for approval</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
